import {Component, inject} from '@angular/core';
import {
  ACCOUNT_LOGIN_ROUTE,
  DEFAULT_ROUTE_CONSTANT,
  DESTINATION_KEY_CONSTANT, FRESH_FROM_SOURCE_CONSTANT, LOGIN_OBJECT_KEY_CONSTANT,
  LOGIN_SUCCESSFUL_ROUTE, PREVIOUS_DESTINATION_KEY_CONSTANT,
  PRODUCT_KEY_CONSTANT,
  REDIRECTED_FLAG_CONSTANT
} from "../constants/routes.constant";
import {from, map, mergeMap, Subject} from "rxjs";
import {SESSION_TOKEN_KEY} from "../constants/local-storage.constant";
import {PROGRESS_BAR_SUBJECT} from "../app.component";
import {FirebaseAuthService} from "../services/firebase-auth.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {AuthenticationService} from "../services/authentication.service";
import {ActivatedRoute, Router} from "@angular/router";
import {Location} from "@angular/common";

@Component({
    selector: 'app-sign-in-with-google',
    templateUrl: './sign-in-with-google.component.html',
    styleUrl: './sign-in-with-google.component.scss',
    standalone: false
})
export class SignInWithGoogleComponent {
  public inProgress: boolean = false;
  private readonly loginSuccessfulRoute: string = LOGIN_SUCCESSFUL_ROUTE;
  private readonly loginRoute: string = ACCOUNT_LOGIN_ROUTE;
  private productName!: string;
  private destination!: string;
  private loginObject!: string;
  private previousDestination!: string;
  private readonly productNameKey: string = PRODUCT_KEY_CONSTANT;
  private readonly destinationKey: string = DESTINATION_KEY_CONSTANT;
  private readonly loginObjectKey: string = LOGIN_OBJECT_KEY_CONSTANT;
  private readonly previousDestinationKey: string = PREVIOUS_DESTINATION_KEY_CONSTANT;
  public queryParams: { [key: string]: string } = {
    [this.productNameKey]: this.productName,
    [this.destinationKey]: this.destination,
    [this.loginObjectKey]: this.loginObject,
    [this.previousDestinationKey]: this.previousDestination,
  };
  private freshFromSourceKey: string = FRESH_FROM_SOURCE_CONSTANT;
  private freshFromSource!: string;
  public subject: Subject<boolean> = PROGRESS_BAR_SUBJECT;

  private readonly snackBar: MatSnackBar = inject(MatSnackBar);
  private readonly firebaseAuthService: FirebaseAuthService = inject(FirebaseAuthService);
  private readonly authenticationService: AuthenticationService = inject(AuthenticationService);
  private readonly router: Router = inject(Router);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly location: Location = inject(Location);

  ngOnInit() {
    this.productName = this.activatedRoute.snapshot.queryParamMap.get(
      this.productNameKey
    )!;
    this.destination = this.activatedRoute.snapshot.queryParamMap.get(
      this.destinationKey
    )!;
    this.loginObject = this.activatedRoute.snapshot.queryParamMap.get(
      this.loginObjectKey
    )!;
    this.previousDestination = this.activatedRoute.snapshot.queryParamMap.get(
      this.previousDestinationKey
    )!;
    this.freshFromSource = this.activatedRoute.snapshot.queryParamMap.get(
      this.freshFromSourceKey
    )!;

    this.queryParams = {
      [this.productNameKey]: this.productName,
      [this.destinationKey]: this.destination,
      [this.loginObjectKey]: this.loginObject,
      [this.previousDestinationKey]: this.previousDestination,
    };

    if (!this.productName || !this.destination) {
      this.router.navigate(DEFAULT_ROUTE_CONSTANT, {
        queryParams: this.queryParams,
      });
    }

    if (!this.freshFromSource) {
      this.completeSignInWithGoogleUsingRedirect();
    } else {
      // reload removing the ffs
      this.removeParamsWithoutNavigation();
      this.signInWithGoogle();
    }
  }

  public removeParamsWithoutNavigation() {
    const currentPath = this.location.path()
      .replace('&ffs=true', ''); // Get the URL path without the query string

    this.location.replaceState(currentPath); // Replace the current state in history
  }

  public signInWithGoogle() {
    this.inProgress = true;
    this.subject.next(true);

    localStorage.removeItem(REDIRECTED_FLAG_CONSTANT);

    from(this.firebaseAuthService.signInWithGoogle()).subscribe({
      next: () => {
        // INFO Nothing here matters because the application is typically closed when the redirect happens
      },
      error: () => {
        this.subject.next(false);
        this.inProgress = false;
        this.snackBar.open('An error occurred!', 'close', {
          verticalPosition: 'top',
          horizontalPosition: 'end',
          panelClass: [
            'bg-danger',
            'text-white',
            'rounded-3e',
            'modified-snackbar',
          ],
        });
      },
    });
  }

  public completeSignInWithGoogleUsingRedirect() {
    localStorage.removeItem(REDIRECTED_FLAG_CONSTANT);

    this.subject.next(true);
    this.inProgress = true;

    from(this.firebaseAuthService.getRedirectResult())
      .pipe(
        mergeMap((userCredentials) => this.firebaseAuthService
            .getIdToken()
            .pipe(map((idTokenResult) => ({ idTokenResult, userCredentials })))
        ),
        mergeMap((d) => {
          return this.authenticationService
            .authenticateUser(d.idTokenResult.token)
            .pipe(map((token) => {
              localStorage.setItem(SESSION_TOKEN_KEY, token);

              return { sessionToken: token, ...d }
            }));
        }),
      )
      .subscribe({
        next: () => {
          this.inProgress = false;
          this.goToLoginSuccessfulRoute();
        },
        error: (error) => {
          console.log(error);

          const queryParams = { ...this.queryParams, [FRESH_FROM_SOURCE_CONSTANT]: 'true' };

          this.router.navigate(
            ['/', ...this.loginRoute.split('/')],
            {
              queryParams,
              queryParamsHandling: 'merge',
            }
          );

          this.subject.next(false);
          this.inProgress = false;
          this.snackBar.open('An error occurred!', 'close', {
            verticalPosition: 'top',
            horizontalPosition: 'end',
            panelClass: [
              'bg-danger',
              'text-white',
              'rounded-3e',
              'modified-snackbar',
            ],
          });
        },
      });
  }

  public goToLoginSuccessfulRoute() {
    this.router.navigate(['/', ...this.loginSuccessfulRoute.split('/')], {
      queryParams: this.queryParams,
      queryParamsHandling: 'preserve',
    });
  }
}
